/**
 * POS · Cups & deposits — the venue-level cup/deposit config (Owner-gated
 * server-side on save). Sets the ONE reusable cup (which strichliste article
 * IS the cup — its StockAdjustment is the cup inventory — plus its deposit
 * price) and the ONE on-site bottle deposit (which active on-site pfand the
 * Deposit-out/Deposit-in buttons charge/refund).
 *
 * Pairs with the per-category "Auto-bundle a cup" flag in the Catalog: a
 * cup-flagged category auto-adds a "Cup out" line for every drink it sells,
 * using the cup configured here. FUNCTIONAL scaffold — plain AntD, real API.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  App as AntApp,
  Button,
  Card,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
} from "antd";

import {
  ApiError,
  api,
  type ArticleRow,
  type PfandValueRow,
} from "../../api/client";
import { formatCents } from "../../utils/dateFormat";

const { Text } = Typography;

export const PosCupsPage = () => {
  const { message } = AntApp.useApp();

  const [articles, setArticles] = useState<ArticleRow[] | null>(null);
  const [onsite, setOnsite] = useState<PfandValueRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Draft form state (euros in the input; cents on the wire).
  const [cupArticleId, setCupArticleId] = useState<number | null>(null);
  const [cupPriceEur, setCupPriceEur] = useState<number | null>(null);
  const [depositPfandId, setDepositPfandId] = useState<number | null>(null);
  const [depositEnabled, setDepositEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [cfg, arts, pf] = await Promise.all([
        api.posGetCupConfig(),
        api.listArticles({ active: true }),
        api.listPfandValues(true, "onsite"),
      ]);
      setArticles(arts);
      setOnsite(pf);
      setCupArticleId(cfg.cup_article_id);
      setCupPriceEur(
        cfg.cup_price_cents == null ? null : cfg.cup_price_cents / 100,
      );
      setDepositPfandId(cfg.deposit_pfand_value_id);
      setDepositEnabled(cfg.deposit_enabled);
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.detail : "Failed to load cup config");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    // A cup article needs a price (the deposit charged on Cup-out); guard so a
    // half-set cup doesn't slip through to the till.
    if (cupArticleId !== null && cupPriceEur == null) {
      message.warning("Set a cup deposit price for the chosen cup article.");
      return;
    }
    setSaving(true);
    try {
      const saved = await api.posPutCupConfig({
        cup_article_id: cupArticleId,
        cup_price_cents:
          cupPriceEur == null ? null : Math.round(cupPriceEur * 100),
        deposit_pfand_value_id: depositPfandId,
        deposit_enabled: depositEnabled,
      });
      setCupArticleId(saved.cup_article_id);
      setCupPriceEur(
        saved.cup_price_cents == null ? null : saved.cup_price_cents / 100,
      );
      setDepositPfandId(saved.deposit_pfand_value_id);
      setDepositEnabled(saved.deposit_enabled);
      message.success("Cup & deposit config saved");
    } catch (e) {
      message.error(e instanceof ApiError ? e.detail : "Save failed");
    } finally {
      setSaving(false);
    }
  }, [cupArticleId, cupPriceEur, depositPfandId, depositEnabled, message]);

  const articleOptions = useMemo(
    () =>
      (articles ?? []).map((a) => ({
        value: a.id,
        label: `${a.name} (#${a.id})`,
      })),
    [articles],
  );

  // The stored cup article may not be in the active list (archived / a leaf id).
  // Surface that rather than silently showing a blank select.
  const cupResolved =
    cupArticleId === null ||
    (articles ?? []).some((a) => a.id === cupArticleId);

  if (loadError) {
    return <Alert type="error" showIcon message="Unavailable" description={loadError} />;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", maxWidth: 640 }}>
      <Typography.Title level={3}>POS · Cups &amp; deposits</Typography.Title>

      <Card title="Reusable cup">
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <div>
            <Text type="secondary">Cup article</Text>
            <Select<number | null>
              showSearch
              allowClear
              placeholder="Pick the article that IS the cup"
              style={{ width: "100%", marginTop: 4 }}
              loading={articles === null}
              value={cupArticleId}
              options={articleOptions}
              optionFilterProp="label"
              onChange={(v) => setCupArticleId((v as number | null) ?? null)}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Its stock adjustments ARE the cup inventory — a Cup-out decrements
              it, a Cup-in returns it. Make it a normal stocked article.
            </Text>
          </div>
          {!cupResolved && (
            <Alert
              type="warning"
              showIcon
              message={`Configured cup article #${cupArticleId} isn't in the active catalog — pick an active article or check it isn't archived.`}
            />
          )}
          <div>
            <Text type="secondary">Cup deposit (charged on Cup-out)</Text>
            <InputNumber
              style={{ width: "100%", marginTop: 4 }}
              min={0}
              step={0.5}
              prefix="€"
              placeholder="e.g. 2.00"
              value={cupPriceEur ?? undefined}
              onChange={(v) =>
                setCupPriceEur(v === null || v === undefined ? null : Number(v))
              }
            />
          </div>
        </Space>
      </Card>

      <Card title="On-site bottle deposit">
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div>
              <Text strong>Bottle pfand enabled</Text>
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                Off → no bottle deposit is charged at the till (even on articles
                that have an on-site pfand set), and the Bottle-in button hides.
              </Text>
            </div>
            <Switch checked={depositEnabled} onChange={setDepositEnabled} />
          </div>
          <div>
            <Text type="secondary">On-site deposit pfand</Text>
            <Select<number | null>
              allowClear
              disabled={!depositEnabled}
              placeholder="No on-site deposit"
              style={{ width: "100%", marginTop: 4 }}
              loading={onsite === null}
              value={depositPfandId}
              options={(onsite ?? []).map((p) => ({
                value: p.id,
                label: `${p.label} (${formatCents(p.cents)})${p.active ? "" : " (archived)"}`,
              }))}
              onChange={(v) => setDepositPfandId((v as number | null) ?? null)}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              The loose liability the Deposit-out / Deposit-in buttons charge &amp;
              refund (bottles, not cups). Must be an active on-site pfand value —
              create one in <b>Settings → Pfand values</b>.
            </Text>
          </div>
        </Space>
      </Card>

      <Button type="primary" loading={saving} onClick={() => void save()}>
        Save cup &amp; deposit config
      </Button>
    </Space>
  );
};

export default PosCupsPage;
